import { useRef, useState, useEffect, useCallback } from 'react';

// ----------------------------------------------------------------------

export type UseNavHoverOptions = {
  delay?: number;
  disabled?: boolean;
};

export function useNavHover<T extends HTMLElement = HTMLElement>(options: UseNavHoverOptions = {}) {
  const { delay = 200, disabled = false } = options;

  const elementRef = useRef<T | null>(null);
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const onOpen = useCallback(() => {
    if (disabled) return;
    clearTimer();
    setOpen(true);
  }, [disabled, clearTimer]);

  const onClose = useCallback(
    (immediate: boolean | unknown = false) => {
      clearTimer();
      if (immediate === true || delay <= 0) {
        setOpen(false);
      } else {
        timeoutRef.current = setTimeout(() => {
          setOpen(false);
        }, delay);
      }
    },
    [clearTimer, delay]
  );

  useEffect(
    () => () => {
      clearTimer();
    },
    [clearTimer]
  );

  return {
    open,
    onOpen,
    onClose,
    anchorEl: elementRef.current,
    elementRef,
    setOpen,
  };
}
